import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { ConfigInit, Packages, PackagesAction, UserSession } from "src/store";
import phPackage from "src/assets/static/img/main/icons/ph_package.png";
import LazyLoad from "vanilla-lazyload";
import { ArrowDownNoTail, ArrowRight, PackageProfileBox, PackageProfileBoxShimmer } from "src/components";
import { PackageProfile } from "src/models";
import { Link } from "react-router-dom";
import { _ } from "src/services";
import { NotFound } from "./not.found";

export const PackageDetail: React.FC = () => {

    //#region dependencies

    const dispatch = useDispatch();
    const { programId } = useParams<any>();
    const packages: Packages = useSelector((state: any) => state.PackagesState);
    const user: UserSession = useSelector((state: any) => state.UserState);
    const config: ConfigInit = useSelector((state: any) => state.ConfigState);

    //#endregion

    //#region properties

    const { id }: any = useParams();

    //#endregion

    //#region states

    const [descExpanded, setDescExpanded] = useState(false);

    //#endregion

    //#region methods

    const toggleDescExpanded = () => setDescExpanded((x) => !x);

    //#endregion

    //#region effects

    useEffect(() => {
        dispatch(PackagesAction.requestPackageDetail(+id));

        return () => {
            dispatch(PackagesAction.clearDetail());
        };

    }, [dispatch, id, user.loggedIn]);

    useEffect(() => {
        const lazyload = new LazyLoad({
            elements_selector: ".lazy",
            callback_error: (element: HTMLElement) => {
                const placeholder = element.getAttribute("data-placeholder");
                if (placeholder) element.setAttribute("src", placeholder);
            }
        });

        lazyload.update();

    }, [packages.currentPackage?.image]);

    //#endregion


    const renderShimmer = () => {
        return (
            <>
                <h1 className="payment-box__title text-right d-flex rtl">
                    {
                        !config.config?.appSetting?.singleSubscriptionId &&
                        <Link title={_("return")} to={programId ? `/program/${programId}` : `${user.user?.hasCurrentSubscription ? "/profile" : ""}/subscription`} className="ml-2">
                            <ArrowRight />
                        </Link>
                    }
                    <span className="shimmer" style={{ height: 25, width: 150 }}></span>
                </h1>
                <div className="payment-box__xtitle text-right py-2 d-flex rtl">
                    <div className="shimmer" style={{ height: "1.4em", width: 250, maxWidth: "100%" }}></div>
                </div>
                <div className="package-detail-header header d-flex align-items-center py-4">
                    <div className="logo shimmer" />
                    <h2 className="shimmer w-50"> </h2>
                </div>
                <div className="package-description d-md-none">
                    <h3 className="package-description--tile disabled">
                        <ArrowDownNoTail />
                        <span>{_("detail")}</span>
                    </h3>
                </div>
                <div className="package-description d-none d-md-flex">
                    <ul>
                        {[...new Array(3)].map((x, index) => (
                            <li key={index} className="empty">
                                <span className="shimmer"></span>
                            </li>
                        ))}
                    </ul>
                </div>
                {
                    [...new Array(3)].map((x, index) => <PackageProfileBoxShimmer key={index} />)
                }
            </>
        );
    };

    const renderDetail = (title: string, image: string, profiles: PackageProfile[], description: string[], purchaseType: string) => {
        const showBack = !config.config?.appSetting?.singleSubscriptionId || programId || user.user?.hasCurrentSubscription;
        return (
            <>
                <h1 className="payment-box__title text-right">
                    <span>{purchaseType === "renew" ? _("durationRenew") : _("durationSelect")}</span>
                    {
                        showBack &&
                        <Link title={_("return")} to={programId ? `/program/${programId}` : `${user.user?.hasCurrentSubscription ? "/profile" : ""}/subscription`} className="ml-2">
                            <ArrowRight />
                        </Link>
                    }
                </h1>
                <div className="payment-box__xtitle text-right py-2">{purchaseType === "renew" ? _("durationRenewDescription") : _("durationSelectDescription")}</div>
                <div className="package-detail-header header d-flex align-items-center py-4">
                    <img alt="" data-src={image}
                        data-placeholder={phPackage}
                        src={phPackage}
                        className={`logo lazy`} />
                    <h2>{title}</h2>
                </div>
                <div className={`package-description${descExpanded ? " expanded" : ""}`}>
                    <h3 className="package-description--tile d-md-none" onClick={toggleDescExpanded}>
                        <ArrowDownNoTail />
                        <span>{_("detail")}</span>
                    </h3>
                    <ul>
                        {description?.map((x, index) => (
                            <li key={index}>{x}</li>
                        ))}
                    </ul>
                </div>
                {
                    profiles.map((x, index) => (
                        <PackageProfileBox {...x} key={index} />
                    ))
                }
            </>
        );
    };

    const wrap = (children: JSX.Element) => (
        <div className="container  page-packages app-theme2 my-4 py-4">
            <div className="row no-gutters">
                <div className="col-12 col-lg-8 col-md-12 offset-lg-2 p-0 mb-5">
                    {children}
                </div>
            </div>
        </div>
    );

    const renderContent = () => {

        if (!packages.currentPackage) return wrap(renderShimmer());
        const { title, image, profiles, description, purchaseType, id } = packages.currentPackage;

        if (id < 0) return (<NotFound />);
        return wrap(renderDetail(title, image, profiles, description, purchaseType));
    };

    return renderContent();
};